import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { zearchService } from '../services/zearchService';

const Zearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  
  const isMounted = useRef(true);
  const isManualSearch = useRef(false);

  // Save search to recent (still needed if you want to keep it; remove if not wanted)
  const saveToRecent = (searchQuery) => {
    // Optional: you can remove this function entirely if you don't want recent searches
    const recent = JSON.parse(localStorage.getItem('zearch_recent') || '[]');
    const updated = [
      { query: searchQuery, timestamp: Date.now() },
      ...recent.filter(item => item.query !== searchQuery)
    ].slice(0, 10);
    localStorage.setItem('zearch_recent', JSON.stringify(updated));
  };

  const performSearch = useCallback(async (searchQuery) => {
    if (!isMounted.current) return;
    
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults(null);
      return;
    }

    setIsLoading(true);

    if (isManualSearch.current) {
      setSearchParams({ q: searchQuery });
    }

    try {
      const data = await zearchService.search(searchQuery, 'all', 12);

      if (isMounted.current) {
        if (data.success) {
          setResults(data.results);
          saveToRecent(searchQuery); // optional
        } else if (data.needsLogin) {
          console.log('🔐 Authentication needed for Zearch');
          setResults({
            ens: [], zpost: [], web: [], news: [], videos: [], images: []
          });
        } else {
          console.log('Zearch search failed:', data.error);
          setResults({
            ens: [], zpost: [], web: [], news: [], videos: [], images: []
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      if (isMounted.current) {
        setResults({
          ens: [], zpost: [], web: [], news: [], videos: [], images: []
        });
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        isManualSearch.current = false;
      }
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce(
      (searchQuery) => {
        if (!searchQuery || searchQuery.trim().length < 2) {
          setResults(null);
          return;
        }
        performSearch(searchQuery);
      },
      1200
    ),
    [performSearch]
  );

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    
    if (urlQuery) {
      setQuery(urlQuery);
      isManualSearch.current = true;
      performSearch(urlQuery);
    }
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    
    return () => {
      isMounted.current = false;
      debouncedSearch.cancel();
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    debouncedSearch.cancel();
    
    if (!value || value.trim().length < 2) {
      setResults(null);
      setSearchParams({});
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    
    debouncedSearch.cancel();
    isManualSearch.current = true;
    performSearch(query);
  };

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setSearchParams({});
    debouncedSearch.cancel();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleResultClick = (result) => {
    if (result.type === 'ens') {
      navigate(`/home/identity?search=${encodeURIComponent(result.title)}`);
    } else if (result.type === 'user') {
      navigate(result.url);
    } else if (result.type === 'post') {
      navigate(result.url);
    } else if (result.url.startsWith('/')) {
      navigate(result.url);
    } else {
      window.open(result.url, '_blank', 'noopener noreferrer');
    }
  };

  const renderResultItem = (result, index) => {
    const isExternal = result.url && (result.url.startsWith('http') || result.url.startsWith('https'));
    
    return (
      <div
        key={`${result.type}-${index}`}
        className="zearch-result-card"
        onClick={() => handleResultClick(result)}
        style={{
          borderLeftColor: result.type === 'ens' ? 'var(--gold)' : 
                          result.type === 'zpost' ? 'var(--dark-red)' : 
                          result.type === 'news' ? '#FF6B6B' :
                          result.type === 'video' ? '#FF4757' :
                          result.type === 'image' ? '#3742FA' : 'var(--border)'
        }}
      >
        <div className="result-header">
          {result.favicon && <img src={result.favicon} alt="" className="result-favicon" />}
          {result.avatar && <img src={result.avatar} alt="" className="result-avatar" />}
          {!result.favicon && !result.avatar && (
            <i className={`result-icon ${
              result.type === 'ens' ? 'fab fa-ethereum' :
              result.type === 'user' ? 'fas fa-user' :
              result.type === 'post' ? 'fas fa-message' :
              result.type === 'news' ? 'fas fa-newspaper' :
              result.type === 'video' ? 'fas fa-play-circle' :
              result.type === 'image' ? 'fas fa-image' : 'fas fa-link'
            }`}></i>
          )}
          
          <div className="result-info">
            <h4 className="result-title">{result.title}</h4>
            {result.display_url && <p className="result-url">{result.display_url}</p>}
            {result.meta && <p className="result-meta">{result.meta}</p>}
          </div>
          
          <span className="result-badge" style={{
            background: result.type === 'ens' ? 'rgba(255, 215, 0, 0.1)' :
                       result.type === 'zpost' ? 'rgba(128, 0, 0, 0.1)' :
                       'rgba(255, 255, 255, 0.05)',
            borderColor: result.type === 'ens' ? 'var(--gold)' :
                        result.type === 'zpost' ? 'var(--dark-red)' :
                        'var(--border)',
            color: result.type === 'ens' ? 'var(--gold)' :
                   result.type === 'zpost' ? 'var(--dark-red)' :
                   'var(--text-light)'
          }}>
            {result.type}
          </span>
        </div>
        
        {result.description && <p className="result-description">{result.description}</p>}
        
        <div className="result-footer">
          {result.timestamp && (
            <span className="result-timestamp">
              {new Date(result.timestamp).toLocaleDateString()}
            </span>
          )}
          {isExternal ? (
            <i className="fas fa-external-link-alt result-external"></i>
          ) : (
            <i className="fas fa-arrow-right result-arrow"></i>
          )}
        </div>
      </div>
    );
  };

  const renderInstantAnswer = () => {
    const answer = results?.instant_answers;
    if (!answer || (!answer.infobox && !answer.faq && !answer.discussions)) {
      return null;
    }

    const displayAnswer = answer.infobox || answer.faq || answer.discussions;
    
    return (
      <div className="zearch-instant-answer">
        <h3 className="zearch-section-title">
          <i className="fas fa-bolt" style={{ color: 'var(--gold)', marginRight: '0.5rem' }}></i>
          Instant Answer
        </h3>
        <div className="instant-answer-card">
          {displayAnswer.description && (
            <p className="instant-answer-text">{displayAnswer.description}</p>
          )}
          {displayAnswer.url && (
            <div className="instant-answer-source">
              <span>Source: {displayAnswer.meta_url?.hostname || 'Brave Search'}</span>
              <a 
                href={displayAnswer.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="instant-answer-link"
              >
                Learn more <i className="fas fa-external-link-alt"></i>
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderResultsSection = (sectionId, sectionTitle, sectionIcon, items) => {
    if (!items || items.length === 0) return null;
    
    return (
      <div className="zearch-category-section">
        <h3 className="zearch-section-title">
          <i className={sectionIcon} style={{ marginRight: '0.5rem' }}></i>
          {sectionTitle}
        </h3>
        <div className="zearch-results-list">
          {items.map((item, index) => renderResultItem(item, index))}
        </div>
      </div>
    );
  };

  return (
    <div className="zearch-container">
      <div className="zearch-hero">
        <div className="zearch-logo">
          <h1 className="gold-logo" style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
            Zearch
          </h1>
          <p className="text-secondary" style={{ fontSize: '1rem', marginBottom: '2rem' }}>
            Privacy-first search powered by Brave
          </p>
        </div>

        <form onSubmit={handleSubmit} className="zearch-input-container">
          <div className="zearch-input-wrapper">
            <i className="fas fa-search zearch-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Search ENS domains, Zpost users, or the web..."
              autoFocus
              className="zearch-input"
            />
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="zearch-clear-btn"
                aria-label="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <button type="submit" className="btn btn-gold zearch-submit-btn">
            <i className="fas fa-search"></i> Search
          </button>
        </form>
      </div>

      {isLoading && (
        <div className="zearch-loading">
          <div className="loading-spinner"></div>
          <p className="text-secondary">
            Searching with Brave Search...
            <br />
            <small>Fetching web, ENS, and Zpost results</small>
          </p>
        </div>
      )}

      {!isLoading && results && (
        <div className="zearch-results">
          {renderInstantAnswer()}

          <div className="zearch-results-grid">
            {renderResultsSection('ens', 'ENS Domains', 'fab fa-ethereum', results.ens)}
            {renderResultsSection('zpost', 'Zpost Community', 'fas fa-users', results.zpost)}
            {renderResultsSection('web', 'Web Results', 'fas fa-globe', results.web)}
            {renderResultsSection('news', 'News', 'fas fa-newspaper', results.news)}
            {renderResultsSection('videos', 'Videos', 'fas fa-play-circle', results.videos)}
            {renderResultsSection('images', 'Images', 'fas fa-image', results.images)}
          </div>

          {(!results.ens?.length && !results.zpost?.length && !results.web?.length && !results.news?.length && !results.videos?.length && !results.images?.length) && (
            <div className="zearch-no-results">
              <i className="fas fa-search" style={{ fontSize: '3rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}></i>
              <h3>No results found</h3>
              <p className="text-secondary">
                Try a different search term
              </p>
            </div>
          )}

          {results.web && results.web.length > 0 && (
            <div className="zearch-summary">
              <p className="text-secondary" style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                Showing {results.web.length} web results · Powered by Brave Search
              </p>
            </div>
          )}
        </div>
      )}

      <div className="zearch-footer">
        <p className="text-secondary" style={{ fontSize: '0.8rem', textAlign: 'center' }}>
          <i className="fas fa-shield-alt" style={{ marginRight: '0.5rem', color: 'var(--gold)' }}></i>
          Privacy-focused search · No tracking · No profiling
        </p>
      </div>
    </div>
  );
};

export default Zearch;
